import React, { useState, useEffect, useRef } from "react";
import { BeatLoader } from "react-spinners";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { appStatus } from "../../../store/modules/app_status/actions";
import { useModalContext } from "../../CustomModal/ModalProvider.jsx";
import { motion } from "framer-motion";
import {
  MainContainer,
  Title,
  HeaderSection,
  StyledButton,
  ButtonContainer,
  Row,
  ShowInput,
  Label,
  StyledSelect,
  SolicitacaoFieldWrap,
  SolicitacaoSelect,
  SolicitacaoTip,
  SolicitacaoTipPointer,
  FocusPriorityBox,
  FocusListCaption,
  FocusList,
  FocusItem,
  FocusIndex,
  FocusMeta,
  FirmsLoadingBox,
  FormContainer,
  SideContainer,
  ContentWrapper,
  EmbeddedRoot,
  RightColumn,
  LeftColumn,
} from "./styles";
import Sidebar from "../../sidebar";
import Projects from "../../projects";
import Users from "../../users";
import Profile from "../../profile";
import KanbanBoard from "../../kanban_board";
import Settings from "../../settings";
import Dashboard from "../../dashboard";
import { projectService } from "../../../services/projectService.js";
import { guardianAreaService } from "../../../services/guardianAreaService.js";
import { guardianAlertService } from "../../../services/guardianAlertService.js";
import {
  buildCreateAlertPayload,
  getCoordinatesFromAlertRequest,
  getRiskDetectionsFromAlertRequest,
  assessAlertCoordinates,
  hasFocusDistance,
  formatFocusDistance,
  formatLatLon,
  getFocusPriorityMessage,
  FOCUS_LIST_CAPTION,
} from "../../../utils/alertRequestCoordinates.js";
import { getCreateAlertErrorMessage } from "../../../utils/createAlertError.js";
import RichTextEditor from "../../RichTextEditor";
import {
  ALERT_OBSERVATION_MAX_LENGTH,
  plainTextToRichText,
  richTextToPlainText,
} from "../../../utils/richText.js";
import { goToGuardianHub } from "../../../utils/appNavigation";

const formatAcquisition = (point) => {
  if (!point?.acq_date) return "";
  const raw = String(point.acq_time ?? "").trim();
  const time = /^\d{4}$/.test(raw) ? `${raw.slice(0, 2)}:${raw.slice(2)}` : raw;
  return `${point.acq_date} ${time}`.trim();
};

const getTodayLocalDate = () => {
  const now = new Date();
  const localDate = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 10);
};

/**
 * @param {object} props
 * @param {boolean} [props.embedded] — renderiza só o formulário (aba do Guardian)
 * @param {() => void} [props.onCancel]
 * @param {() => void} [props.onSuccess]
 * @param {object} [props.alertRequest] — solicitação vinculada (senão usa location.state)
 * @param {object} [props.prefillAlert] — { project, guardian_area } vindo da área (senão location.state)
 */
export const FormNewAlert = ({
  embedded = false,
  onCancel,
  onSuccess,
  alertRequest: alertRequestProp = null,
  prefillAlert: prefillAlertProp = null,
} = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { showError, showSuccess, showWarning, showInfo } = useModalContext();

  // Estados do formulário
  const [formData, setFormData] = useState({
    project: "",
    alert_type: "",
    severity_level: "",
    coordinates: "",
    user: "",
    guardian_area: "",
    observation: "",
    alert_date: getTodayLocalDate(),
  });
  const [options, setOptions] = useState({
    projects: [],
    alertTypes: [],
    severityLevels: [],
    users: [],
    guardian_areas: [],
  });
  const [filteredGuardianAreas, setFilteredGuardianAreas] = useState([]);
  const [linkedAlertRequest, setLinkedAlertRequest] = useState(null);
  const [alertRequestId, setAlertRequestId] = useState("");
  const [pendingAlertRequests, setPendingAlertRequests] = useState([]);
  const [loadingAlertRequests, setLoadingAlertRequests] = useState(false);
  const [loadingFirmsContext, setLoadingFirmsContext] = useState(
    () => Boolean((alertRequestProp || location.state?.alertRequest)?.id)
  );
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const prefillAppliedRef = useRef(false);
  const severityLevelsRef = useRef(options.severityLevels);
  severityLevelsRef.current = options.severityLevels;

  const app_status = useSelector((state) => state.app_status.status);

  // Prefere a solicitação já carregada (detalhe/lista da navegação). Não depende
  // só do select — se o ID for limpo por race na lista pendente, a lista de
  // focos/distância ainda usa linkedAlertRequest.
  const selectedAlertRequestForCoords =
    (linkedAlertRequest?.id &&
    (!alertRequestId || linkedAlertRequest.id === alertRequestId)
      ? linkedAlertRequest
      : null) ||
    pendingAlertRequests.find((r) => r.id === alertRequestId) ||
    null;

  const focusPoints = selectedAlertRequestForCoords
    ? getRiskDetectionsFromAlertRequest(selectedAlertRequestForCoords)
    : [];
  const firmsPointsCount = focusPoints.length;
  const focusRanked = hasFocusDistance(focusPoints);
  const focusPriorityMessage = getFocusPriorityMessage(selectedAlertRequestForCoords);

  // Carregar opções (projetos, tipos, severidades, áreas) uma única vez.
  // Não chama /users/ aqui: a lista é só ADM e quebrava o formulário para ENG.
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [projectsRes, alertTypesRes, severityLevelsRes, guardian_areaRes] =
          await Promise.all([
            projectService.getProjects(),
            guardianAlertService.getAlertTypes(),
            guardianAlertService.getSeverityLevels(),
            guardianAreaService.getGuardianAreas(),
          ]);

        setOptions({
          projects: projectsRes.projects_project || projectsRes || [],
          alertTypes: alertTypesRes || [],
          severityLevels: severityLevelsRes || [],
          users: [],
          guardian_areas: guardian_areaRes || [],
        });
      } catch (error) {
        console.error("Erro ao carregar opções:", error);
        setError("Erro ao carregar dados do formulário");
      }
    };
    fetchOptions();
  }, []);

  const incomingAlertRequest = alertRequestProp || location.state?.alertRequest || null;
  const incomingAlertRequestId = incomingAlertRequest?.id || "";

  const applyAlertRequestToForm = (ar) => {
    if (!ar?.id) return;
    setLinkedAlertRequest(ar);
    setAlertRequestId(ar.id);

    const area = ar.area_detail;
    const projectId = area?.project_detail?.id ?? area?.project ?? ar.project_detail?.id ?? ar.project_id;
    const guardianKey = area?.id_code ?? area?.id;

    const obsParts = [ar.description, ar.other_description].filter(Boolean);
    const observation = plainTextToRichText(
      obsParts.join("\n\n").slice(0, ALERT_OBSERVATION_MAX_LENGTH)
    );
    const coords = getCoordinatesFromAlertRequest(ar);
    const alertDate =
      ar.requested_at && String(ar.requested_at).length >= 10
        ? String(ar.requested_at).slice(0, 10)
        : "";
    const levels = severityLevelsRef.current;
    const defaultSeverityLevel = levels.length > 0 ? String(levels[0].id) : "";

    setFormData((prev) => ({
      ...prev,
      ...(projectId != null && projectId !== "" ? { project: String(projectId) } : {}),
      ...(guardianKey != null && guardianKey !== "" ? { guardian_area: String(guardianKey) } : {}),
      ...(ar.alert_type_detail?.id != null ? { alert_type: String(ar.alert_type_detail.id) } : {}),
      severity_level: prev.severity_level || defaultSeverityLevel,
      observation: observation || prev.observation,
      coordinates: coords || prev.coordinates,
      alert_date: alertDate || prev.alert_date,
    }));
  };

  // A solicitação da navegação pode vir sem focos/distância. Relê o detalhe
  // antes de mostrar coordenadas, pontos e distância até o KML.
  useEffect(() => {
    if (!incomingAlertRequestId) {
      setLoadingFirmsContext(false);
      return;
    }

    let cancelled = false;
    setLoadingFirmsContext(true);
    if (incomingAlertRequest) applyAlertRequestToForm(incomingAlertRequest);

    (async () => {
      try {
        const full = await guardianAlertService.getAlertRequest(incomingAlertRequestId);
        if (!cancelled) applyAlertRequestToForm(full);
      } catch (err) {
        // Endpoint de detalhe pode estar ausente/desatualizado no backend.
        // Mantém o pré-preenchimento com o objeto da navegação/lista.
        console.error("Erro ao ler focos da solicitação:", err);
        if (!cancelled && incomingAlertRequest) {
          applyAlertRequestToForm(incomingAlertRequest);
        }
      } finally {
        if (!cancelled) setLoadingFirmsContext(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // A releitura depende só da solicitação. Severidade entra pelo ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingAlertRequestId]);

  useEffect(() => {
    if (!incomingAlertRequestId || !options.severityLevels.length) return;
    const fallback = String(options.severityLevels[0].id);
    setFormData((prev) =>
      prev.severity_level ? prev : { ...prev, severity_level: fallback }
    );
  }, [incomingAlertRequestId, options.severityLevels]);

  // Prefill a partir da página de detalhes da área (projeto + área)
  useEffect(() => {
    if (prefillAppliedRef.current) return;

    const ar = alertRequestProp || location.state?.alertRequest;
    if (ar?.id) return; // solicitação tem prioridade

    const prefill = prefillAlertProp || location.state?.prefillAlert;
    if (!prefill?.project && !prefill?.guardian_area) return;
    // Aguarda opções carregarem para o filtro de áreas funcionar
    if (!options.guardian_areas?.length && !options.projects?.length) return;

    prefillAppliedRef.current = true;
    setFormData((prev) => ({
      ...prev,
      ...(prefill.project ? { project: String(prefill.project) } : {}),
      ...(prefill.guardian_area
        ? { guardian_area: String(prefill.guardian_area) }
        : {}),
    }));
  }, [
    prefillAlertProp,
    location.state,
    alertRequestProp,
    options.guardian_areas,
    options.projects,
  ]);

  // Filtrar áreas conforme o projeto selecionado
  useEffect(() => {
    if (formData.project) {
      const areasDoProjeto = options.guardian_areas.filter(
        (area) => String(area.project_detail?.id) === String(formData.project)
      );
      setFilteredGuardianAreas(areasDoProjeto);
    } else {
      setFilteredGuardianAreas([]);
    }
  }, [formData.project, options.guardian_areas]);

  // Buscar solicitações pendentes para a área selecionada (para o select "Solicitação (opcional)")
  useEffect(() => {
    if (!formData.guardian_area) {
      setPendingAlertRequests([]);
      return;
    }

    const areaId = formData.guardian_area;
    let cancelled = false;
    (async () => {
      setLoadingAlertRequests(true);
      try {
        const data = await guardianAlertService.getAlertRequests({
          area: areaId,
          pending_only: true,
        });
        let list = Array.isArray(data) ? data : data?.results ?? [];
        // Incluir a solicitação que veio via location.state (caso ainda não esteja na lista)
        const ar = linkedAlertRequest;
        const linkedAreaKey = ar?.area_detail?.id_code ?? ar?.area_detail?.id;
        if (ar?.id && linkedAreaKey != null && String(linkedAreaKey) === String(areaId) && !list.some((r) => r.id === ar.id)) {
          list = [ar, ...list];
        }
        if (!cancelled) setPendingAlertRequests(list);
      } catch (err) {
        console.error("Erro ao listar solicitações pendentes:", err);
        if (!cancelled) setPendingAlertRequests([]);
      } finally {
        if (!cancelled) setLoadingAlertRequests(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [formData.guardian_area, linkedAlertRequest]);

  // Se a solicitação vinculada não estiver mais na lista (ex: foi removida), limpar o ID.
  // Não limpa enquanto houver linkedAlertRequest do fluxo "criar a partir de solicitação".
  useEffect(() => {
    if (loadingFirmsContext || loadingAlertRequests) return;
    if (!alertRequestId || pendingAlertRequests.length === 0) return;
    if (linkedAlertRequest?.id === alertRequestId) return;
    const stillThere = pendingAlertRequests.some((r) => r.id === alertRequestId);
    if (!stillThere) setAlertRequestId("");
  }, [
    pendingAlertRequests,
    alertRequestId,
    loadingFirmsContext,
    loadingAlertRequests,
    linkedAlertRequest,
  ]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    // Área Guardian é obrigatória — sem ela o POST não deve sair
    if (!String(formData.guardian_area || "").trim()) {
      await showError(
        "Erro!",
        "Por favor, certifique-se de que uma área foi selecionada ao criar um alerta"
      );
      return;
    }

    // Validação dos demais campos obrigatórios do modelo
    const required = {
      alert_type: "Tipo de Alerta",
      severity_level: "Nível de Severidade",
      coordinates: "Coordenadas",
    };
    const missing = Object.keys(required).filter((field) => !formData[field]);
    if (missing.length) {
      await showError("Erro!", `Preencha: ${missing.map((f) => required[f]).join(", ")}`);
      return;
    }

    if (richTextToPlainText(formData.observation).length > ALERT_OBSERVATION_MAX_LENGTH) {
      await showError(
        "Erro!",
        `Os detalhes do alerta devem ter no máximo ${ALERT_OBSERVATION_MAX_LENGTH} caracteres.`
      );
      return;
    }

    setSubmitting(true);
    try {
      const assessment = assessAlertCoordinates(
        formData.coordinates,
        selectedAlertRequestForCoords
      );

      // Normaliza o campo (ex.: vírgula decimal BR → ponto) antes do envio
      if (
        assessment.normalizedCoordinates &&
        assessment.normalizedCoordinates !== formData.coordinates
      ) {
        setFormData((prev) => ({
          ...prev,
          coordinates: assessment.normalizedCoordinates,
        }));
      }

      if (assessment.hasIssues) {
        await showWarning("Atenção às coordenadas", assessment.message);
      } else {
        await showInfo("Coordenadas verificadas", assessment.message);
      }

      const payload = buildCreateAlertPayload(
        {
          ...formData,
          coordinates:
            assessment.normalizedCoordinates || formData.coordinates,
        },
        {
          alertRequest: selectedAlertRequestForCoords,
          alertRequestId: alertRequestId || undefined,
        }
      );

      await guardianAlertService.createAlert(payload);
      await showSuccess(
        "Sucesso!",
        "Alerta cadastrado com sucesso! Os e-mails e as notificações para celular estão sendo enviados em segundo plano."
      );
      if (typeof onSuccess === "function") {
        onSuccess({ alertRequestId: alertRequestId || null });
      } else if (alertRequestId) {
        goToGuardianHub(dispatch, navigate, {
          state: { activeTab: "requests" },
        });
      } else {
        goToGuardianHub(dispatch, navigate, {
          state: { activeTab: "alerts" },
        });
      }
    } catch (error) {
      console.error("❌ Detalhes do erro:", error.response?.data);
      await showError("Erro!", getCreateAlertErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = (e) => {
    e.preventDefault();
    if (typeof onCancel === "function") {
      onCancel();
      return;
    }
    if (alertRequestId) {
      goToGuardianHub(dispatch, navigate, {
        state: { activeTab: "requests" },
      });
    } else {
      goToGuardianHub(dispatch, navigate, {
        state: { activeTab: "panel" },
      });
    }
  };

  if (error) return <div style={{ padding: "20px", color: "var(--danger)" }}>Erro: {error}</div>;

  const formBody = (
          <ContentWrapper $embedded={embedded}>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.8 }} />
            <HeaderSection>
              {!embedded && <Title>Novo Alerta</Title>}
              <p style={{ color: "var(--text-heading)", textAlign: "center", marginTop: embedded ? "0" : "-1rem", fontSize: "0.95rem" }}>
                Opcionalmente vincule a uma <strong>solicitação de alerta</strong> pendente da mesma área Guardian.
                Sem seleção, o alerta é criado <strong>sem</strong> vínculo de solicitação.
              </p>
              {linkedAlertRequest?.id && (
                <p style={{ color: "var(--text-heading)", textAlign: "center", marginTop: "0.25rem", fontSize: "0.9rem" }}>
                  Você veio da solicitação{" "}
                  <strong>{linkedAlertRequest.cod_alerta || "sem código"}</strong>{" "}
                  — ajuste o vínculo abaixo se necessário.
                </p>
              )}
              {!linkedAlertRequest?.id &&
                (prefillAlertProp || location.state?.prefillAlert)?.guardian_area && (
                <p style={{ color: "var(--text-heading)", textAlign: "center", marginTop: "0.25rem", fontSize: "0.9rem" }}>
                  Projeto e área pré-selecionados a partir de{" "}
                  <strong>
                    {(prefillAlertProp || location.state?.prefillAlert)?.area_name ||
                      "a área Guardian"}
                  </strong>
                  .
                </p>
              )}
            </HeaderSection>

            <FormContainer onSubmit={handleSubmit} noValidate $embedded={embedded}>
              <Row>
                <LeftColumn><Label>Projeto:</Label></LeftColumn>
                <RightColumn>
                  <StyledSelect id="project" name="project" value={formData.project} onChange={handleInputChange} required>
                    <option value="">Selecione um projeto</option>
                    {options.projects.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name || p.title}
                      </option>
                    ))}
                  </StyledSelect>
                </RightColumn>
              </Row>

              <Row>
                <LeftColumn><Label>Área:</Label></LeftColumn>
                <RightColumn>
                  <StyledSelect id="guardian_area" name="guardian_area" value={formData.guardian_area} onChange={handleInputChange} required>
                    <option value="">Selecione uma área</option>
                    {filteredGuardianAreas.map((area) => (
                      <option key={area.id_code} value={area.id_code}>
                        {area.name || area.title}
                      </option>
                    ))}
                  </StyledSelect>
                </RightColumn>
              </Row>

              <Row>
                <LeftColumn><Label>Solicitação:</Label></LeftColumn>
                <RightColumn>
                  <SolicitacaoFieldWrap>
                    <SolicitacaoSelect
                      id="alert_request_select"
                      value={alertRequestId}
                      $highlight={!alertRequestId}
                      onChange={(e) => {
                        const id = e.target.value;
                        setAlertRequestId(id);
                        const req = pendingAlertRequests.find((r) => r.id === id);
                        if (req) {
                          const coords = getCoordinatesFromAlertRequest(req);
                          if (coords) {
                            setFormData((prev) => ({ ...prev, coordinates: coords }));
                          }
                        }
                      }}
                      disabled={!formData.guardian_area || loadingAlertRequests}
                    >
                      <option value="">
                        {loadingAlertRequests
                          ? "Carregando solicitações..."
                          : !formData.guardian_area
                            ? "Selecione uma área Guardian primeiro"
                            : "Nenhuma (sem vínculo)"}
                      </option>
                      {pendingAlertRequests.map((req) => {
                        const label =
                          req.cod_alerta ||
                          (req.description
                            ? req.description.slice(0, 60)
                            : "Solicitação sem código");
                        const descExtra =
                          req.cod_alerta && req.description
                            ? ` — ${req.description.slice(0, 60)}${req.description.length > 60 ? "…" : ""}`
                            : "";
                        return (
                          <option key={req.id} value={req.id}>
                            {label}
                            {descExtra}
                          </option>
                        );
                      })}
                    </SolicitacaoSelect>

                    {!alertRequestId ? (
                      <SolicitacaoTip>
                        <SolicitacaoTipPointer aria-hidden>☝️</SolicitacaoTipPointer>
                        <span>
                          <strong>Recomendado:</strong> vincule uma solicitação quando houver.
                          O campo é opcional, mas facilita o rastreio e a resposta do alerta.
                        </span>
                      </SolicitacaoTip>
                    ) : null}

                    {formData.guardian_area && !loadingAlertRequests && pendingAlertRequests.length === 0 && (
                      <div style={{ fontSize: "11px", color: "var(--text-heading)" }}>
                        Nenhuma solicitação pendente para esta área.
                      </div>
                    )}
                  </SolicitacaoFieldWrap>
                </RightColumn>
              </Row>

              <Row>
                <LeftColumn><Label>Coordenadas:</Label></LeftColumn>
                <RightColumn>
                  <ShowInput
                    type="text"
                    id="coordinates"
                    name="coordinates"
                    value={formData.coordinates}
                    onChange={handleInputChange}
                    placeholder={
                      loadingFirmsContext
                        ? "Lendo coordenadas da solicitação…"
                        : "Ex: -2.37834, -57.23727"
                    }
                    disabled={loadingFirmsContext}
                    required
                  />
                  <div
                    style={{
                      fontSize: "11px",
                      color: "var(--text-muted)",
                      marginTop: "6px",
                    }}
                  >
                    Use ponto decimal (ou vírgula decimal BR: -2,37834, -57,23727).
                    Lat −90…90 · Lon −180…180. Coordenadas inválidas geram aviso, mas não impedem o cadastro.
                  </div>
                  {loadingFirmsContext && (
                    <FirmsLoadingBox role="status" aria-live="polite">
                      <BeatLoader size={10} color="var(--accent)" loading />
                      <span>
                        Lendo os focos de incêndio, as coordenadas e a distância
                        até o perímetro do KML…
                      </span>
                    </FirmsLoadingBox>
                  )}
                  {!loadingFirmsContext && focusPriorityMessage && (
                    <FocusPriorityBox>
                      <strong>PRIORIDADE</strong> — {focusPriorityMessage}
                    </FocusPriorityBox>
                  )}
                  {!loadingFirmsContext && firmsPointsCount > 0 && (
                    <>
                      <FocusListCaption>
                        {focusRanked && <>{FOCUS_LIST_CAPTION}. </>}
                        {firmsPointsCount} foco(s) FIRMS válido(s) da solicitação. Todos
                        serão enviados no e-mail ao cliente; clique em um foco para usá-lo
                        como coordenada de referência.
                      </FocusListCaption>
                      <FocusList>
                        {focusPoints.map((p, idx) => {
                          const coords = formatLatLon(p.lat, p.lon);
                          const meta = [
                            formatAcquisition(p),
                            p.firms_query_source || p.satellite,
                          ]
                            .filter(Boolean)
                            .join(" · ");
                          const distance = formatFocusDistance(p);
                          return (
                            <FocusItem
                              key={`${coords}-${p.acq_date || ""}-${p.acq_time || ""}-${idx}`}
                              $priority={focusRanked && idx === 0}
                              $selected={formData.coordinates === coords}
                              title="Usar como coordenada de referência"
                              onClick={() =>
                                setFormData((prev) => ({ ...prev, coordinates: coords }))
                              }
                            >
                              <FocusIndex>{idx + 1}.</FocusIndex>
                              <span>
                                {coords}
                                {distance && (
                                  <>
                                    {" — "}
                                    <strong>{distance}</strong>
                                  </>
                                )}
                                {formData.coordinates === coords && " (referência)"}
                                {meta && <FocusMeta>{meta}</FocusMeta>}
                              </span>
                            </FocusItem>
                          );
                        })}
                      </FocusList>
                    </>
                  )}
                </RightColumn>
              </Row>

              <Row>
                <LeftColumn><Label>Tipo de Alerta:</Label></LeftColumn>
                <RightColumn>
                  <StyledSelect id="alert_type" name="alert_type" value={formData.alert_type} onChange={handleInputChange} required>
                    <option value="">Selecione o tipo de alerta</option>
                    {options.alertTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name || type.description}
                      </option>
                    ))}
                  </StyledSelect>
                </RightColumn>
              </Row>

              <Row>
                <LeftColumn><Label>Nível de Severidade:</Label></LeftColumn>
                <RightColumn>
                  <StyledSelect id="severity_level" name="severity_level" value={formData.severity_level} onChange={handleInputChange} required>
                    <option value="">Selecione o nível de severidade</option>
                    {options.severityLevels.map((level) => (
                      <option key={level.id} value={level.id}>
                        {level.name || level.description}
                      </option>
                    ))}
                  </StyledSelect>
                </RightColumn>
              </Row>

              <Row>
                <LeftColumn><Label>Detalhes:</Label></LeftColumn>
                <RightColumn>
                  <RichTextEditor
                    id="observation"
                    value={formData.observation}
                    onChange={(html) =>
                      setFormData((prev) => ({ ...prev, observation: html }))
                    }
                    maxLength={ALERT_OBSERVATION_MAX_LENGTH}
                    placeholder="Descreva aqui detalhes e demais informações do alerta."
                    disabled={submitting}
                  />
                </RightColumn>
              </Row>

              <Row>
                <LeftColumn><Label>Data do Alerta:</Label></LeftColumn>
                <RightColumn>
                  <ShowInput type="date" id="alert_date" name="alert_date" value={formData.alert_date} onChange={handleInputChange} required />
                </RightColumn>
              </Row>

              <ButtonContainer>
                <StyledButton type="submit" disabled={submitting || loadingFirmsContext}>
                  {loadingFirmsContext
                    ? "Lendo informações…"
                    : submitting
                      ? "Salvando..."
                      : "Salvar Alerta"}
                </StyledButton>
                <StyledButton type="button" onClick={handleCancel} disabled={submitting}>
                  Cancelar
                </StyledButton>
              </ButtonContainer>
            </FormContainer>
          </ContentWrapper>
  );

  if (embedded) {
    return <EmbeddedRoot>{formBody}</EmbeddedRoot>;
  }

  return (
    <MainContainer>
      <Sidebar />
      <SideContainer>
        {formBody}
      </SideContainer>
    </MainContainer>
  );
};